import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Icon from '../components/ui/icon';
import { promotionApi } from '../services/promotionApi';
import { Promotion, TrendingProduct, getDiscountTypeText, formatPromotionDiscount, getBrandTierColor, getBrandTierText, getTrendScoreColor, getTrendScoreText, formatProductPrice, isPromotionActive } from '../types/promotion';

const Promotions: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [trendingProducts, setTrendingProducts] = useState<TrendingProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('promotions');

  // Load promotions and trending products data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load promotions and trending products
        const [promotionsData, trendingData] = await Promise.all([
          promotionApi.getActivePromotions(),
          promotionApi.getTopTrendingProducts(10)
        ]);
        
        // Filter promotions to only show currently active ones (by time)
        const currentlyActivePromotions = promotionsData.filter(promotion => 
          isPromotionActive(promotion.start_date, promotion.end_date)
        );
        
        setPromotions(currentlyActivePromotions);
        setTrendingProducts(trendingData);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Không thể tải danh sách khuyến mãi');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle promotion actions
  const handleViewPromotion = (promotion: Promotion) => {
    const message = `Cho tôi xem chi tiết chương trình khuyến mãi "${promotion.name}" nhé!`;
    navigate('/assistant', { state: { initialMessage: message } });
  };

  const handleOrderWithPromotion = (promotion: Promotion) => {
    const message = `Cho tôi xem chi tiết khuyến mãi ${promotion.name}, xem có áp dụng cho cửa hàng của tôi không?`;
    navigate('/assistant', { state: { initialMessage: message } });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold mb-3">💰 Khuyến mãi</h1>
        <p className="text-md text-muted-foreground px-4">Chương trình khuyến mãi hấp dẫn đang diễn ra & những sản phẩm đang hot trend</p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="alert-circle" size={20} className="text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex justify-center mt-4">
        <div className="flex bg-secondary rounded-lg p-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('promotions')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'promotions' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon name="gift" size={16} />
            Khuyến mãi ({promotions.length})
          </button>
          <button
            onClick={() => setActiveTab('trending')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'trending' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon name="trending-up" size={16} />
            Sản phẩm hot ({trendingProducts.length})
          </button>
        </div>
      </div>

      {/* Promotions Tab */}
      {activeTab === 'promotions' && (
        <div className="space-y-4 mt-4">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex justify-between items-center">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                      <div className="h-10 bg-gray-200 rounded w-32"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Promotions List */}
          {!isLoading && (
            <div className="space-y-4">
              {promotions.map((promotion) => (
                <Card key={promotion.promotion_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-xl font-bold leading-tight">{promotion.name}</CardTitle>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700">
                              {getDiscountTypeText(promotion.discount_type)}
                            </Badge>
                            <Badge className="bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700">
                              Đang diễn ra
                            </Badge>
                          </div>
                          <p className="text-base text-muted-foreground leading-relaxed">
                            {promotion.description}
                          </p>
                          <div className="flex items-center gap-4 text-sm">
                            <span>Bắt đầu từ {new Date(promotion.start_date).toLocaleDateString('vi-VN')} đến hết ngày {new Date(promotion.end_date).toLocaleDateString('vi-VN')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xl md:text-2xl font-bold text-orange-600">
                            {formatPromotionDiscount(promotion.discount_type, promotion.discount_value)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="purple" 
                      className="text-sm px-4 py-2"
                      onClick={() => handleViewPromotion(promotion)}
                    >
                      <Icon name="info" size={16} />
                      <span>Tìm hiểu</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="blue"
                      className="text-sm px-4 py-2"
                      onClick={() => handleOrderWithPromotion(promotion)}
                    >
                      <Icon name="shopping-cart" size={16} />
                      <span>Đặt hàng</span> 
                    </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State for Promotions */}
          {!isLoading && promotions.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Icon name="gift" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Chưa có khuyến mãi đang diễn ra</h3>
                <p className="text-muted-foreground">Hãy quay lại sau để xem ưu đãi mới</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Trending Products Tab */}
      {activeTab === 'trending' && (
        <div className="space-y-4 mt-4">
          {/* Loading State */}
          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex justify-between items-center">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                      <div className="h-8 bg-gray-200 rounded w-24"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Trending Products Grid */}
          {!isLoading && (
            <div className="grid gap-4 md:grid-cols-2">
              {trendingProducts.map((product) => (
                <Card key={product.sku} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle className="text-lg font-bold leading-tight">{product.name}</CardTitle>
                          <Badge className={getBrandTierColor(product.brand_tier)}>
                            {getBrandTierText(product.brand_tier)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {product.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className={getTrendScoreColor(product.trend_score)}>
                            📈 {getTrendScoreText(product.trend_score)}
                          </span>
                          <span className="text-muted-foreground">
                            🙃 Được giới trẻ yêu thích
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">
                          {product.pack_size} • {product.unit}
                        </div>
                        <div className="font-bold text-lg text-green-600">
                          {formatProductPrice(product.price)}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="purple"
                        className="text-sm px-3 py-2"
                        onClick={() => navigate('/assistant', { 
                          state: { 
                            initialMessage: `Cho tôi xem chi tiết sản phẩm ${product.name} nhé!` 
                          } 
                        })}
                      >
                        <Icon name="info" size={16} />
                        <span>Tìm hiểu</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State for Trending Products */}
          {!isLoading && trendingProducts.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Icon name="trending-up" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Chưa có sản phẩm trending</h3>
                <p className="text-muted-foreground">Hãy quay lại sau để xem sản phẩm hot mới</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Promotions;